import { useContext } from 'react';
import ThemeContext from '../context/ThemeContext';

/**
 * Theme-aware colours for the two job-chat surfaces: `JobChatDrawer` and
 * `ChatNotificationToast`.
 *
 * WHY THIS EXISTS AND WHY IT IS NOT A SET OF CLASSES. Both components are
 * styled entirely with inline style objects and carry no class attribute at
 * all. Every light-mode remap in `theme.css` is keyed on a class selector, so
 * all of them miss these two files — there is nothing for them to match. That
 * is not a near-miss, it is the whole remap layer being absent: in light mode
 * the drawer heading measured 1.23:1, and an incoming message body measured
 * 1.00:1 against its own bubble, which is to say the text and the bubble
 * resolved to the same colour and the message rendered as an empty box.
 *
 * `useChatColor()` returns a picker, `c(darkValue, lightValue)`. Every call
 * site passes the exact literal that site has always shipped as `darkValue`,
 * so dark mode is unchanged BY CONSTRUCTION rather than by inspection —
 * `JobChatDrawer.dark-parity.test.tsx` asserts that site by site.
 *
 * THE ONE DELIBERATE DARK CHANGE is the accent pair, which does not go through
 * this picker: an own-message bubble and the Send label were white on amber at
 * 2.15:1 in BOTH themes. `theme.css` already decided that question twice — it
 * puts #262626 on `.bg-fieldiq-accent` in light and sets `--status-fg` to
 * near-navy in dark — so near-black text on a bright fill is the house rule and
 * these two sites were the outlier. They now use `hsl(var(--primary))` over
 * `hsl(var(--primary-foreground))`, which is amber over near-black in both
 * themes. The fill shifts a hair in dark (#f59e0b to #f5a623) because #f59e0b
 * was never the brand gold; it is `index.css`'s older copy of the accent, and
 * `CLAUDE.md` asks for brand colours through variables rather than literals.
 */
export const CHAT_TOKENS = {
  /** Headings and any text that has to carry weight. */
  heading: 'var(--fieldiq-heading)',
  /** Message bodies. */
  text: 'var(--fieldiq-text)',
  /** Timestamps, sender names, placeholders, dismissed states. */
  muted: 'var(--fieldiq-muted)',
  /** A raised pill on the panel — the disabled Send button. */
  card: 'var(--fieldiq-card)',
  /** Hairlines. */
  border: 'var(--fieldiq-border)',
  /** The recessed tint a deleted message sits on. */
  recessed: 'hsl(var(--muted))',
  /** The light-mode field fill, which `theme.css` already forces on inputs. */
  inputFill: 'var(--fieldiq-input-bg)',
} as const;

/**
 * The live-connection dot. `--status-active` is #4ade80 in dark — byte-for-byte
 * the literal this dot already used — and green-700 in light, where it clears
 * the 3:1 a non-text indicator needs. The lighter greens do not: the general
 * success token measures 2.35:1 on the drawer header.
 */
export const CHAT_DOT_ONLINE = 'var(--status-active)';

/** Amber fill and the near-black that reads on it. Same pairing in both themes. */
export const CHAT_ACCENT_FILL = 'hsl(var(--primary))';
export const CHAT_ACCENT_TEXT = 'hsl(var(--primary-foreground))';

/**
 * The accent when it has to be SEEN rather than fill a surface — the toast's
 * left rule. See the token's own note in `theme.css`: it is the same gold in
 * dark and amber-700 in light, where the gold would sit at 1.74:1.
 */
export const CHAT_ACCENT_SIGNAL = 'var(--fieldiq-accent-signal)';

export type ChatColorPicker = (darkValue: string, lightValue: string) => string;

/**
 * Reads the theme without requiring a provider. Both components are mounted
 * under `ThemeProvider` in the app, but `useJobChat.socket-ticket.test.tsx`
 * renders the toast bare, and `useTheme()` throws there. Dark is the right
 * default: it is what the `:root` block resolves to before any attribute is
 * set on the document element.
 */
export function useChatColor(): ChatColorPicker {
  const ctx = useContext(ThemeContext);
  const theme = ctx?.theme ?? 'dark';
  return (darkValue, lightValue) => (theme === 'light' ? lightValue : darkValue);
}
